import './index.scss';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Link } from '../../atoms/Link';
import { LinkPreviewIcon } from '../../atoms/Iconography';
import { LinkPreviewInfo } from './LinkPreviewInfo';
import { buildSupportedLinkPreview } from './supportedLinkPreview';
import { SupportedLinkPreview } from './types';
import { fetchLinkPreview } from '../../../services/linkPreviewService';

interface LinkWithPreviewProps {
  url: string;
  className?: string;
  showTextLink?: boolean;
}

export const LinkWithPreview: React.FC<LinkWithPreviewProps> = ({
  url,
  className,
  showTextLink = true,
}) => {
  const parsedPreview = React.useMemo(() => buildSupportedLinkPreview(url), [url]);
  const [preview, setPreview] = useState(parsedPreview);

  useEffect(() => {
    setPreview(parsedPreview);
  }, [parsedPreview]);

  useEffect(() => {
    if (!parsedPreview || typeof fetch !== 'function') {
      return undefined;
    }

    let isCancelled = false;
    const abortController = typeof AbortController === 'function' ? new AbortController() : null;

    fetchLinkPreview(
      url,
      abortController ? { signal: abortController.signal } : {},
    )
      .then((resolvedPreview: SupportedLinkPreview) => {
        if (isCancelled) {
          return;
        }

        setPreview(resolvedPreview);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') {
          return;
        }
      });

    return () => {
      isCancelled = true;
      abortController?.abort();
    };
  }, [parsedPreview, url]);

  const handleLinkClick = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  if (!preview) {
    return showTextLink ? (
      <Link to={url} external onClick={handleLinkClick}>
        {url}
      </Link>
    ) : null;
  }

  return (
    <div className={`link-with-preview${className ? ' ' + className : ''}`.trim()}>
      {showTextLink && (
        <Link to={url} external onClick={handleLinkClick}>
          {url}
        </Link>
      )}
      <a
        className="link-preview-card"
        href={preview.canonicalUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleLinkClick}
        aria-label={preview.ariaLabel}
      >
        <div className="link-preview-content">
          <div className="link-preview-media">
            {preview.artworkUrl ? (
              <img
                className="link-preview-artwork"
                src={preview.artworkUrl}
                alt={`${preview.title} artwork`}
                width={72}
                height={72}
                loading="lazy"
              />
            ) : (
              <LinkPreviewIcon className="link-preview-icon" />
            )}
          </div>
          <LinkPreviewInfo
            eyebrow={preview.eyebrow}
            title={preview.title}
            subtitle={preview.subtitle}
            meta={preview.meta}
          />
          <span className="link-preview-arrow" aria-hidden="true">↗</span>
        </div>
      </a>
    </div>
  );
};

export default LinkWithPreview;
